import { useCallback, useContext, useEffect, useMemo } from 'react';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';

import { UnitSystemContext } from 'utils/measurement';

const UNIT_SYSTEM_PREFERENCE_KEY = 'cadViewer:unitSystemPreference';

type UseUnitSystemPreferenceReturn = {
  preferredUnitSystem: PreferredUnitSystem;
  setPreferredUnitSystem: (unitSystem: PreferredUnitSystem) => void;
};

/**
 * Hook that manages the user's personal unit system preference in localStorage
 * Initializes with the user group's preference from UnitSystemContext
 *
 * @returns Object containing current preference, setter, and reset function
 */
export const useUnitSystemPreference = (): UseUnitSystemPreferenceReturn => {
  const unitSystemContext = useContext(UnitSystemContext);

  // Get group preference from context (fallback to Imperial if context not available)
  const groupPreference = useMemo(() => {
    return (
      unitSystemContext?.preferredUnitSystem ?? PREFERRED_UNIT_SYSTEMS.IMPERIAL
    );
  }, [unitSystemContext?.preferredUnitSystem]);

  // Initialize localStorage value on first load
  useEffect(() => {
    try {
      const stored = localStorage.getItem(UNIT_SYSTEM_PREFERENCE_KEY);
      if (!stored) {
        // Initialize with group preference if no stored value
        localStorage.setItem(UNIT_SYSTEM_PREFERENCE_KEY, groupPreference);
      } else {
        unitSystemContext?.setPreferredUnitSystem(
          stored as PreferredUnitSystem,
        );
      }
    } catch (error) {
      console.warn('localStorage not available:', error);
    }
  });

  const setPreferredUnitSystem = useCallback(
    (unitSystem: PreferredUnitSystem) => {
      try {
        localStorage.setItem(UNIT_SYSTEM_PREFERENCE_KEY, unitSystem);
        unitSystemContext?.setPreferredUnitSystem(unitSystem);
      } catch (error) {
        console.warn(
          'Failed to save unit system preference to localStorage:',
          error,
        );
      }
    },
    [unitSystemContext],
  );

  // Read directly from localStorage, falling back to group preference
  const getCurrentPreference = (): PreferredUnitSystem => {
    try {
      const stored = localStorage.getItem(UNIT_SYSTEM_PREFERENCE_KEY);
      if (stored) {
        return stored as PreferredUnitSystem;
      }
    } catch (error) {
      console.warn('Failed to read from localStorage:', error);
    }

    return groupPreference;
  };

  const currentPreference = getCurrentPreference();

  return {
    preferredUnitSystem: currentPreference,
    setPreferredUnitSystem,
  };
};
